import React from 'react';
import { Text } from 'components/chroma';
import { ICONS } from 'components/Icons';
import { toPercentage } from 'scenes/common/helpers/percentage';
import { InsuranceInfoType, InsuranceName } from 'scenes/common/flows/types';
import {
  carInsurancePageUrl,
  carInsuranceQuoteUrl,
  homeInsurancePageUrl,
  homeInsuranceQuoteUrl,
  landlordInsurancePageUrl,
  landlordInsuranceQuoteUrl,
  specialOffersUrl,
  tAndCInsDiscountUrl,
} from 'scenes/common/helpers/contacts';
import {
  toCurrencyWithSign,
  TWO_DECIMAL_PLACES,
} from 'scenes/common/helpers/currency';

const DEFAULT_ONLINE_INSURANCE_DISCOUNT = 15;

const InsuranceDiscount: {
  [key in InsuranceName]: number;
} = {
  [InsuranceName.Car]: 15,
  [InsuranceName.Home]: 30,
  [InsuranceName.Landlord]: 20,
};

export const DEFAULT_ONLINE_CAMPAIGN = {
  image: ICONS.LOGO.FIFTEEN_PERCENT_DISCOUNT,
  discount: DEFAULT_ONLINE_INSURANCE_DISCOUNT,
  description: `Get a quote in minutes!`,
  desktopDescription: `Buy online ${String.fromCharCode(
    38
  )} Save ${toPercentage(DEFAULT_ONLINE_INSURANCE_DISCOUNT)}`,
  descriptionAfterQuote: `Buy online ${String.fromCharCode(
    38
  )} Save ${toPercentage(DEFAULT_ONLINE_INSURANCE_DISCOUNT)}`,
  termsModalHeading: `Buy online and Save ${toPercentage(
    DEFAULT_ONLINE_INSURANCE_DISCOUNT
  )}`,
  termsLinkText: `T&Cs apply. Minimum premiums may apply.`,
  disclaimer: (
    <Text>
      Minimum premiums may apply. This online discount offer may be withdrawn at
      any time. Applies to new policies only. For full terms and conditions, see{' '}
      <a href={tAndCInsDiscountUrl()} target="_blank" rel="noopener noreferrer">
        here
      </a>
      .
    </Text>
  ),
};

const calculateCombinedPolicyDiscount = (discount: number): number =>
  discount - DEFAULT_ONLINE_CAMPAIGN.discount;

const combinedDiscountBreakdown = (discount: number): string =>
  calculateCombinedPolicyDiscount(discount) > 0
    ? `The ${toPercentage(discount)} discount is made up of the ${toPercentage(
        DEFAULT_ONLINE_CAMPAIGN.discount
      )} online discount and a ${toPercentage(
        calculateCombinedPolicyDiscount(discount)
      )} combined policy discount. `
    : '';

export const INSURANCE_INFO: InsuranceInfoType[] = [
  {
    name: InsuranceName.Car,
    image: ICONS.DEFAULT.MOTOR,
    discount: InsuranceDiscount.Car,
    saveOnlineText: `Save ${toPercentage(InsuranceDiscount.Car)} online`,
    termsLinkText: 'T&Cs apply. Minimum premiums may apply.',
    termsModalHeading: `Discount on new quotes`,
    links: {
      findOutMore: carInsurancePageUrl(),
      getQuote: carInsuranceQuoteUrl(),
    },
    termsAndConditionsElement: (
      <Text>
        Minimum premiums may apply. This online discount offer may be withdrawn
        at any time. Applies to new policies only. For full terms and
        conditions, see{' '}
        <a href={specialOffersUrl()} target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </Text>
    ),
  },
  {
    name: InsuranceName.Home,
    image: ICONS.DEFAULT.HOME,
    discount: InsuranceDiscount.Home,
    saveOnlineText: `Save up to ${toPercentage(InsuranceDiscount.Home)} online`,
    termsLinkText: 'T&Cs apply. Minimum premiums may apply.',
    termsModalHeading: `Discount on new quotes`,
    links: {
      findOutMore: homeInsurancePageUrl(),
      getQuote: homeInsuranceQuoteUrl(),
    },
    termsAndConditionsElement: (
      <Text>
        {combinedDiscountBreakdown(InsuranceDiscount.Home)}
        Minimum premiums may apply. This discount offer may be withdrawn at any
        time. Applies to new policies only. For full terms and conditions, see{' '}
        <a href={specialOffersUrl()} target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </Text>
    ),
  },
  {
    name: InsuranceName.Landlord,
    image: ICONS.DEFAULT.LANDLORD,
    discount: InsuranceDiscount.Landlord,
    // saveOnlineText: `Save ${toPercentage(InsuranceDiscount.Landlord)} online`,
    saveOnlineText: `Save up to ${toPercentage(
      InsuranceDiscount.Landlord
    )} online`, // Business wants to display 10% discount for LL in the cross-sell tiles for now.
    termsLinkText: 'T&Cs apply. Minimum premiums may apply.',
    termsModalHeading: `Discount on new quotes`,
    links: {
      findOutMore: landlordInsurancePageUrl(),
      getQuote: landlordInsuranceQuoteUrl(),
    },
    termsAndConditionsElement: (
      <Text>
        {combinedDiscountBreakdown(InsuranceDiscount.Landlord)}
        Minimum premiums may apply. This discount offer may be withdrawn at any
        time. Applies to new policies only. For full terms and conditions, see{' '}
        <a href={specialOffersUrl()} target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </Text>
    ),
  },
];

export const insuranceDiscount = (insuranceName: InsuranceName) =>
  INSURANCE_INFO.find((insurance) => insurance.name === insuranceName)
    ?.discount ?? DEFAULT_ONLINE_CAMPAIGN.discount;

export const combinedPolicyDiscount = (insuranceName: InsuranceName) =>
  insuranceDiscount(insuranceName) - DEFAULT_ONLINE_CAMPAIGN.discount;

export const getInstalmentFootnote = (
  isMonthlyPaymentPreselected: boolean,
  monthlyInstalmentsTotal: string,
  savedAmount: number
) =>
  isMonthlyPaymentPreselected
    ? `Monthly instalments will total ${toCurrencyWithSign(
        monthlyInstalmentsTotal,
        TWO_DECIMAL_PLACES
      )}.`
    : `${
        savedAmount > 0
          ? `Paying annually saves ${toCurrencyWithSign(
              savedAmount,
              TWO_DECIMAL_PLACES
            )}.`
          : 'Monthly payment options are available.'
      }`;
